import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Mail, ArrowLeft, UserCircle } from 'lucide-react';
import axios from 'axios';
import { getPostAuthRedirect } from '../lib/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function SignupPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('options'); // 'options', 'email', 'verify', 'login', 'forgot', 'reset-code', 'new-password', 'guest'
  const [guestName, setGuestName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleGoogleLogin = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = encodeURIComponent(window.location.origin + '/auth/google/callback');
    const scope = encodeURIComponent('openid email profile');
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}&access_type=offline&prompt=select_account`;
  };

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/email/signup`, { email, password, name });
      setMode('verify');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/auth/email/verify`, { email, code: verificationCode });
      if (res.data.token) localStorage.setItem('noyo_token', res.data.token);
      navigate(getPostAuthRedirect(), { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/auth/email/login`, { email, password });
      if (res.data.token) localStorage.setItem('noyo_token', res.data.token);
      navigate(getPostAuthRedirect(), { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    try {
      await axios.post(`${API}/auth/email/resend-code?email=${encodeURIComponent(email)}`);
      setError('');
    } catch (err) {
      setError('Failed to resend code');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/email/forgot-password`, { email });
      setMode('reset-code');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/auth/email/reset-password`, {
        email,
        code: resetCode,
        new_password: newPassword
      });
      setSuccessMessage(res.data.message);
      setMode('login');
      setResetCode('');
      setNewPassword('');
      setPassword('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGuestLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/auth/guest`, { name: guestName });
      if (res.data.token) localStorage.setItem('noyo_token', res.data.token);
      navigate(getPostAuthRedirect(), { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create guest account');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <BookOpen className="w-10 h-10 text-primary" />
            <h1 className="text-3xl font-serif font-semibold text-foreground">NOYO</h1>
          </div>
          <p className="text-sm text-muted-foreground mb-1">Your life stories, in one place.</p>
          <p className="text-muted-foreground">
            {mode === 'email' && 'Sign up with email'}
            {mode === 'verify' && 'Verify your email'}
            {mode === 'login' && 'Welcome back'}
            {mode === 'forgot' && 'Reset your password'}
            {mode === 'guest' && 'Try as guest'}
            {mode === 'reset-code' && 'Enter reset code'}
          </p>
        </div>

        {/* Content Card */}
        <div className="bg-card border border-border rounded-2xl p-8 shadow-lg">

          {/* Options View */}
          {mode === 'options' && (
            <div className="space-y-4">
              <p className="text-center text-sm text-muted-foreground mb-2">Create your account</p>
              <button
                onClick={handleGoogleLogin}
                className="w-full bg-primary text-white hover:bg-primary/80 rounded-full px-6 py-4 font-medium transition-all duration-300 shadow-sm hover:shadow-md active:scale-95 flex items-center justify-center space-x-3"
                data-testid="google-login-button"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#ffffff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#ffffff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#ffffff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#ffffff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Continue with Google</span>
              </button>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-card px-4 text-muted-foreground">or</span>
                </div>
              </div>

              <button
                onClick={() => setMode('email')}
                className="w-full bg-card border border-border text-foreground hover:bg-secondary rounded-full px-6 py-4 font-medium transition-all duration-300 shadow-sm hover:shadow-md active:scale-95 flex items-center justify-center space-x-3"
                data-testid="email-signup-option"
              >
                <Mail className="w-5 h-5" />
                <span>Sign up with Email</span>
              </button>

              <p className="text-center text-sm text-muted-foreground mt-6">
                Already have an account?{' '}
                <button onClick={() => setMode('login')} className="text-primary hover:underline font-medium" data-testid="switch-to-login">
                  Sign in
                </button>
              </p>

              <button
                onClick={() => setMode('guest')}
                className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors mt-2 flex items-center justify-center space-x-2"
                data-testid="guest-login-option"
              >
                <UserCircle className="w-4 h-4" />
                <span>Try as Guest</span>
              </button>
            </div>
          )}

          {/* Guest Login Form */}
          {mode === 'guest' && (
            <form onSubmit={handleGuestLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">What's your name?</label>
                <input
                  type="text"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="Enter your name"
                  required
                  className="w-full bg-card border border-border focus:border-primary focus:ring-primary/20 rounded-xl px-4 py-3 text-base"
                  data-testid="guest-name-input"
                />
              </div>
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <button
                type="submit"
                disabled={isLoading || !guestName.trim()}
                className="w-full bg-primary text-white hover:bg-primary/80 rounded-full px-6 py-4 font-medium transition-all duration-300 shadow-sm hover:shadow-md active:scale-95 disabled:opacity-50"
                data-testid="guest-submit-button"
              >
                {isLoading ? 'Creating...' : 'Continue as Guest'}
              </button>
              <p className="text-xs text-muted-foreground text-center mt-3">
                By continuing you agree to our <a href="/terms" className="text-primary hover:underline">Terms & Privacy Policy</a>
              </p>
              <button
                type="button"
                onClick={() => { setMode('options'); setError(''); }}
                className="w-full text-muted-foreground hover:text-foreground text-sm mt-2"
              >
                ← Back to options
              </button>
            </form>
          )}

          {/* Email Signup Form */}
          {mode === 'email' && (
            <form onSubmit={handleEmailSignup} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-card border border-border focus:border-primary focus:ring-primary/20 rounded-xl px-4 py-3"
                  placeholder="Your name"
                  required
                  data-testid="signup-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-card border border-border focus:border-primary focus:ring-primary/20 rounded-xl px-4 py-3"
                  placeholder="you@example.com"
                  required
                  data-testid="signup-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-card border border-border focus:border-primary focus:ring-primary/20 rounded-xl px-4 py-3"
                  placeholder="Min 6 characters"
                  required
                  minLength={6}
                  data-testid="signup-password-input"
                />
              </div>

              {error && (
                <p className="text-red-500 text-sm text-center">{error}</p>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary text-white hover:bg-primary/80 rounded-full px-6 py-4 font-medium transition-all duration-300 shadow-sm hover:shadow-md active:scale-95 disabled:opacity-50"
                data-testid="signup-submit-button"
              >
                {isLoading ? 'Sending code...' : 'Continue'}
              </button>

              <p className="text-xs text-muted-foreground text-center mt-3">
                By signing up you agree to our <a href="/terms" className="text-primary hover:underline">Terms & Privacy Policy</a>
              </p>

              <button
                type="button"
                onClick={() => { setMode('options'); setError(''); }}
                className="w-full text-muted-foreground hover:text-foreground text-sm mt-2"
              >
                ← Back to options
              </button>
            </form>
          )}

          {/* Verification Code Form */}
          {mode === 'verify' && (
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <div className="text-center mb-4">
                <Mail className="w-12 h-12 text-primary mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  We sent a 6-digit code to<br />
                  <span className="font-medium text-foreground">{email}</span>
                </p>
              </div>

              <div>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full bg-card border border-border focus:border-primary focus:ring-primary/20 rounded-xl px-4 py-4 text-center text-2xl tracking-[0.5em] font-mono"
                  placeholder="000000"
                  maxLength={6}
                  required
                  data-testid="verification-code-input"
                />
              </div>

              {error && (
                <p className="text-red-500 text-sm text-center">{error}</p>
              )}

              <button
                type="submit"
                disabled={isLoading || verificationCode.length !== 6}
                className="w-full bg-primary text-white hover:bg-primary/80 rounded-full px-6 py-4 font-medium transition-all duration-300 shadow-sm hover:shadow-md active:scale-95 disabled:opacity-50"
                data-testid="verify-submit-button"
              >
                {isLoading ? 'Verifying...' : 'Verify & Create Account'}
              </button>

              <p className="text-center text-sm text-muted-foreground">
                Didn't receive a code?{' '}
                <button type="button" onClick={handleResendCode} className="text-primary hover:underline font-medium">
                  Resend
                </button>
              </p>
            </form>
          )}

          {/* Login Form */}
          {mode === 'login' && (
            <div className="space-y-4">
              <button
                type="button"
                onClick={handleGoogleLogin}
                className="w-full bg-primary text-white hover:bg-primary/80 rounded-full px-6 py-4 font-medium transition-all duration-300 shadow-sm hover:shadow-md active:scale-95 flex items-center justify-center space-x-2"
                data-testid="login-google-button"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#ffffff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#ffffff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#ffffff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#ffffff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Continue with Google</span>
              </button>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-card px-4 text-muted-foreground">or sign in with email</span>
                </div>
              </div>
            </div>
          )}
          {mode === 'login' && (
            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-card border border-border focus:border-primary focus:ring-primary/20 rounded-xl px-4 py-3"
                  placeholder="you@example.com"
                  required
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-card border border-border focus:border-primary focus:ring-primary/20 rounded-xl px-4 py-3"
                  placeholder="Your password"
                  required
                  data-testid="login-password-input"
                />
              </div>

              {error && (
                <p className="text-red-500 text-sm text-center">{error}</p>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary text-white hover:bg-primary/80 rounded-full px-6 py-4 font-medium transition-all duration-300 shadow-sm hover:shadow-md active:scale-95 disabled:opacity-50"
                data-testid="login-submit-button"
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </button>

              {successMessage && (
                <p className="text-sm text-green-600 text-center" data-testid="success-message">{successMessage}</p>
              )}

              <div className="text-center">
                <button type="button" onClick={() => { setMode('forgot'); setError(''); setSuccessMessage(''); }} className="text-sm text-muted-foreground hover:text-primary transition-colors" data-testid="forgot-password-link">
                  Forgot password?
                </button>
              </div>

              <p className="text-center text-sm text-muted-foreground mt-4">
                Don't have an account?{' '}
                <button type="button" onClick={() => { setMode('options'); setError(''); }} className="text-primary hover:underline font-medium" data-testid="switch-to-signup">
                  Sign up
                </button>
              </p>
            </form>
          )}

          {/* Forgot Password Form */}
          {mode === 'forgot' && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="text-center mb-4">
                <Mail className="w-12 h-12 text-primary mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  Enter your email and we'll send you a code to reset your password.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-card border border-border focus:border-primary focus:ring-primary/20 rounded-xl px-4 py-3"
                  placeholder="you@example.com"
                  required
                  data-testid="forgot-email-input"
                />
              </div>

              {error && (
                <p className="text-red-500 text-sm text-center">{error}</p>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary text-white hover:bg-primary/80 rounded-full px-6 py-4 font-medium transition-all duration-300 shadow-sm hover:shadow-md active:scale-95 disabled:opacity-50"
                data-testid="forgot-submit-button"
              >
                {isLoading ? 'Sending...' : 'Send Reset Code'}
              </button>

              <button
                type="button"
                onClick={() => { setMode('login'); setError(''); }}
                className="w-full text-muted-foreground hover:text-foreground text-sm mt-2"
              >
                ← Back to sign in
              </button>
            </form>
          )}

          {/* Reset Code + New Password Form */}
          {mode === 'reset-code' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="text-center mb-4">
                <Mail className="w-12 h-12 text-primary mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  We sent a 6-digit code to<br />
                  <span className="font-medium text-foreground">{email}</span>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Reset Code</label>
                <input
                  type="text"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full bg-card border border-border focus:border-primary focus:ring-primary/20 rounded-xl px-4 py-4 text-center text-2xl tracking-[0.5em] font-mono"
                  placeholder="000000"
                  maxLength={6}
                  required
                  data-testid="reset-code-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full bg-card border border-border focus:border-primary focus:ring-primary/20 rounded-xl px-4 py-3"
                  placeholder="Min 6 characters"
                  required
                  minLength={6}
                  data-testid="new-password-input"
                />
              </div>

              {error && (
                <p className="text-red-500 text-sm text-center">{error}</p>
              )}

              <button
                type="submit"
                disabled={isLoading || resetCode.length !== 6}
                className="w-full bg-primary text-white hover:bg-primary/80 rounded-full px-6 py-4 font-medium transition-all duration-300 shadow-sm hover:shadow-md active:scale-95 disabled:opacity-50"
                data-testid="reset-password-submit-button"
              >
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </button>

              <button
                type="button"
                onClick={() => { setMode('forgot'); setError(''); setResetCode(''); setNewPassword(''); }}
                className="w-full text-muted-foreground hover:text-foreground text-sm mt-2"
              >
                ← Back
              </button>
            </form>
          )}
        </div>

      </motion.div>
    </div>
  );
}

function LandingPage() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('noyo_token');
    if (token) {
      axios.get(`${API}/auth/me`).then(res => {
        if (res.data && res.data.user_id) {
          navigate(getPostAuthRedirect(), { replace: true });
        } else {
          localStorage.removeItem('noyo_token');
          setChecking(false);
        }
      }).catch(() => {
        localStorage.removeItem('noyo_token');
        setChecking(false);
      });
    } else {
      setChecking(false);
    }
  }, [navigate]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <BookOpen className="w-10 h-10 text-primary animate-pulse" strokeWidth={1.5} />
      </div>
    );
  }

  return <SignupPage />;
}

function LoginPage() {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/signup', { replace: true });
  }, [navigate]);
  return null;
}

export default LandingPage;
export { SignupPage, LoginPage };
